import { useTranslation } from 'react-i18next';
import { FaRegTrashAlt, FaSyncAlt } from 'react-icons/fa';

import { PermissionKeys, TenantMemberReadModel } from '@client/api';
import { isDefinedString, Select, TableCell, TableRow, Text } from '@client/toolkit';
import { PermissionBasedActionButton } from '../../../../../components/permission-based-button/permission-based-button';
import { useUserPermission } from '../../../../../hooks/useUserPermission/useUserPermission';

interface CompanyUserRowProps {
  user?: TenantMemberReadModel;
  isFetching?: boolean;
  isCurrentUser?: boolean;
  onClickDelete?: (user: TenantMemberReadModel) => void;
  onStatusChange?: (user: TenantMemberReadModel, newStatus: 'Active' | 'Inactive') => void;
  onResendInvite?: (user: TenantMemberReadModel) => void;
}

export const CompanyUserRow = ({
  user,
  isFetching,
  isCurrentUser,
  onClickDelete,
  onStatusChange,
  onResendInvite,
}: CompanyUserRowProps) => {
  const { t: tType } = useTranslation('type');
  const isInactive = user?.status === 'Inactive';
  const isInvited = user?.status === 'Invited';

  const { hasPermission } = useUserPermission();

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (user && onStatusChange) {
      onStatusChange(user, e.target.value as 'Active' | 'Inactive');
    }
  };

  return (
    <TableRow className={isInactive ? 'opacity-60' : ''}>
      <TableCell loading={isFetching} className="align-middle">
        <Text isEmpty={!isDefinedString(user?.name)}>
          {user?.name}
        </Text>
      </TableCell>
      <TableCell loading={isFetching} className="align-middle">
        <Text isEmpty={!isDefinedString(user?.email)}>{user?.email}</Text>
      </TableCell>
      <TableCell loading={isFetching} className="align-middle">
        <Text isEmpty={!isDefinedString(user?.culture?.language)}>
          {user?.culture?.language ? tType(`Language.${user.culture.language}`) : '-'}
        </Text>
      </TableCell>
      <TableCell loading={isFetching} className="align-middle">
        <Text isEmpty={!isDefinedString(user?.culture?.timezone)}>{user?.culture?.timezone ?? '-'}</Text>
      </TableCell>
      <TableCell loading={isFetching} className="align-middle">
        <div className="flex items-center gap-2">
          <Text isEmpty={!isDefinedString(user?.role)}>{user?.role ? tType(`UserRole.${user.role}`) : '-'}</Text>
          {isCurrentUser && <span className="text-xs text-brand-primary font-medium">({tType('you')})</span>}
        </div>
      </TableCell>
      <TableCell loading={isFetching} className="align-middle">
        {isInvited ? (
          <div className="flex flex-col">
            <Text className="text-info">{tType('TenantMemberStatus.Invited')}</Text>
            {user?.invitedAt && (
              <Text className="text-xs text-secondary">
                {new Date(user.invitedAt).toLocaleDateString(undefined, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Text>
            )}
          </div>
        ) : isCurrentUser || !hasPermission([PermissionKeys.Tenant.UserUpdate]) ? (
          <Text className="text-secondary">{tType(`TenantMemberStatus.${user?.status ?? 'Active'}`)}</Text>
        ) : (
          <Select
            value={user?.status ?? 'Active'}
            onChange={handleStatusChange}
            disabled={isFetching || !user}
            className={`text-sm h-9 ${isInactive ? 'text-warning' : ''}`}
          >
            <option value="Active">{tType('TenantMemberStatus.Active')}</option>
            <option value="Inactive">{tType('TenantMemberStatus.Inactive')}</option>
          </Select>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap pl-4 align-middle" align="right">
        {isInvited && (
          <div className="flex items-center justify-end gap-2">
            <PermissionBasedActionButton
              size="xs"
              color="custom"
              disabled={!onResendInvite || !user || isFetching}
              onClick={() => (onResendInvite && user ? onResendInvite(user) : undefined)}
              className="text-brand-primary bg-surface-elevated size-9"
              permissions={[PermissionKeys.Tenant.CompanyUserInvite]}
            >
              <FaSyncAlt />
            </PermissionBasedActionButton>
            <PermissionBasedActionButton
              color="custom"
              size="xs"
              onClick={() => (onClickDelete && user ? onClickDelete(user) : undefined)}
              disabled={!onClickDelete || !user}
              permissions={[PermissionKeys.Tenant.UserDelete]}
              className="text-brand-primary bg-surface-elevated size-9"
            >
              <FaRegTrashAlt />
            </PermissionBasedActionButton>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};
